import React from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { Divider, IconButton, Stack } from '@mui/material';
import { Command, tabActions } from '@store/slices';
import { AddOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Button } from '@common/Button';
import { Label } from '@common/Label';
import { useTheme } from '../../../../styles/theme';
import { ModuleParameter, NodeField } from '@components/Flow/Nodes';
import { BasicInput } from '@common/Expression/Basic';
import { useDispatch } from 'react-redux';
import { Select } from '@common/Select';
import { Option } from '@common/BucketInput/BucketField';
import { ErrorMessage } from '@common/Autocomplete';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  & .MuiInputAdornment-root {
    margin-right: 10px;
  }
`;

const Action = styled.div`
  display: flex;
  align-items: center;

  svg {
    font-size: 2ch;
  }
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const ModuleParameters: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { state } = command;

  if (!field || !command) return null;

  const value = state[field.id] as ModuleParameter[];

  const addParameter = () => {
    const value: ModuleParameter = {
      id: uuidv4(),
      name: '',
      value: '',
      description: '',
      type: 'string',
      required: true,
    };

    const input = {
      nodeId: command.node.id,
      fieldId: field.id,
      value: value,
    };

    dispatch(tabActions.nodeInputArrayPush(input));
    dispatch(tabActions.setSaved(false));
  };

  const updateField = (nestedFieldId: string, fieldName: string, newValue: string) => {
    const input = {
      nodeId: command.node.id,
      fieldId: field.id,
      nestedFieldId,
      fieldName,
      value: newValue,
    };

    dispatch(tabActions.nodeInputArrayUpdate(input));
    dispatch(tabActions.setSaved(false));
  };

  const deleteField = (nestedFieldId: string) => {
    dispatch(
      tabActions.nodeInputArrayFilter({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldId,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const isDuplicateName = (name: string): boolean => {
    let count = 0;

    for (const param of value) {
      if (param.name && param.name === name) {
        count++;
        if (count > 1) {
          return true;
        }
      }
    }

    return false;
  };

  const searchTypeOptions: Option[] = [
    { label: 'string', value: 'string' },
    { label: 'boolean', value: 'boolean' },
    { label: 'enum', value: 'enum' },
    { label: 'number', value: 'number' },
  ];

  return (
    <Container>
      <Label title={field.label} tooltip={field.description} />
      <Stack spacing={2} divider={<Divider flexItem />}>
        {(value || [])?.map((param, index) => (
          <Stack key={index} spacing={1}>
            <Stack display='grid' gridTemplateColumns='7fr 3fr 42px' gap='8px'>
              <BasicInput
                key={`${command.node.id}-${field.id}-name`}
                keyProp={`${command.node.id}-${field.id}`}
                value={param.name}
                setValue={(newValue: string) => {
                  updateField(param.id, 'name', newValue);
                }}
                placeholder='param'
                minRows={1}
                maxRows={1}
                wrapping={false}
              />
              <Select
                key={`${command.node.id}-${field.id}-type`}
                value={param.type}
                setValue={(value: string) => updateField(param.id, 'type', value)}
                options={searchTypeOptions}
                placeholder='Select an index search type'
              />
              <Action>
                <IconButton
                  sx={{ border: `1px solid ${theme.line.light}`, height: '42px', width: '42px' }}
                  onClick={() => deleteField(param.id)}
                >
                  <DeleteOutlineOutlined sx={{ fontSize: '1.4ch !important', color: theme.text.subtext }} />
                </IconButton>
              </Action>
            </Stack>
            <BasicInput
              key={`${command.node.id}-${field.id}-description`}
              keyProp={`${command.node.id}-${field.id}`}
              value={param.description}
              setValue={(newValue: string) => {
                updateField(param.id, 'description', newValue);
              }}
              placeholder='description'
              minRows={1}
              maxRows={1}
              wrapping={false}
            />
            {isDuplicateName(param.name) && <ErrorMessage>A module parameter already uses this name.</ErrorMessage>}
          </Stack>
        ))}
        <Button color='primary' fullWidth onClick={addParameter}>
          <AddOutlined sx={{ fontSize: '14px', color: `${theme.text.standard} !important` }} />
        </Button>
      </Stack>
    </Container>
  );
};
